import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type SupplierRequestDetail } from './types';
import { zodSupplierRequestDetailResponseSchema } from './validations';

/**
 * Fetches the detail of a single supplier request by ID.
 *
 * @param reqId - The UUID of the supplier request.
 * @param options - Optional request options including abort signal.
 * @returns The supplier request detail response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Requests/operation/get_request_vadmin_supplier_requests__req_id__get
 */
export const fetchSupplierRequest = async (
  reqId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.SUPPLIER_REQUEST.GET(reqId), {
        ...options,
        validate: {
          ...options?.validate,
          response: zodSupplierRequestDetailResponseSchema,
        },
      })
      .json<SupplierRequestDetail>();
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching supplier request',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.supplierRequest.get.error404, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.supplierRequest.get.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
